import { Icon } from '@ur-solutions/react-components'
import { Card } from '@/components_deprecated/Card'
import React, { useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router'
import styled from 'styled-components'
import type { BaseProps } from '@/types/props'
import { usePermissions } from '@/utils/hooks'

const Item = styled(NavLink)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 1rem 1.8rem;

  color: ${props => props.theme.colors.gray4};
  font-weight: 600;

  &.active {
    background-color: white;
    box-shadow: ${props => props.theme.shadow.default};
    color: ${props => props.theme.colors.text.dark};
  }
  &:hover {
    text-decoration: none;
    color: ${props => props.theme.colors.secondary};
  }

  ${props => props.theme.media.mobile} {
    padding: 1rem;
  }
`

const Wrapper = styled(Card)`
  display: flex;
  flex-direction: column;
  padding: 0;

  background-color: rgba(255, 255, 255, 0.3);
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  overflow: hidden;

  ${Item} + ${Item} {
    border-top: solid 1px ${props => props.theme.colors.gray9};
  }
`

const Expandable = styled.div``

interface ExpanderProps {
  expanded: boolean
}
const Expander = styled.div<ExpanderProps>`
  --duration: 0.25s;

  display: grid;
  grid-template-rows: ${props => (props.expanded ? '1fr' : '0fr')};
  overflow: hidden;
  transition: grid-template-rows var(--duration) ease-out;

  & > div.content {
    min-height: 0;
    visibility: ${props => (props.expanded ? 'visible' : 'hidden')};
    transition: visibility var(--duration);
  }
`

export interface LinkListItem {
  text: string
  href: string
  permissions?: string[] | string
  hasModule?: boolean
  /** Match only if end of route matches. Default: true */
  end?: boolean
  /** If set, find selected element by this regex */
  matchBy?: RegExp
}

interface LinkListProps extends BaseProps {
  items: LinkListItem[]
  expandable?: boolean
}

export const LinkList: React.FC<LinkListProps> = ({
  className,
  id,
  items,
  expandable = false,
}) => {
  const location = useLocation()
  const { hasPermissions } = usePermissions()

  const [expanded, setExpanded] = useState(false)

  const filteredItems = useMemo(
    () =>
      items.filter(item =>
        !item?.hasModule && typeof item.hasModule !== 'undefined'
          ? false
          : typeof item.permissions === 'undefined'
          ? true
          : hasPermissions(item.permissions)
      ),
    [hasPermissions, items]
  )

  const [selected, unselected] = useMemo<[LinkListItem, LinkListItem[]]>(() => {
    const firstItem = filteredItems[0]
    if (!firstItem) return [{ text: '', href: '' }, []]

    if (!expandable) return [firstItem, []]

    return filteredItems.reduce<[LinkListItem, LinkListItem[]]>(
      (acc, item) => {
        const matched =
          typeof item.matchBy !== 'undefined'
            ? item.matchBy.test(location.pathname)
            : location.pathname.startsWith(item.href)

        if (matched) acc[0] = item
        else acc[1].push(item)
        return acc
      },
      [firstItem, []]
    )
  }, [expandable, filteredItems, location.pathname])

  return (
    <Wrapper className={className} id={id}>
      {!expandable ? (
        filteredItems.map(item => (
          <Item key={item.href} to={item.href} end={item.end ?? true}>
            {item.text}
          </Item>
        ))
      ) : (
        <Expandable aria-expanded={expanded}>
          <div className="selected">
            <Item
              to="#"
              end={selected.end}
              onClick={() => setExpanded(v => !v)}
            >
              {selected.text}
              <Icon
                icon={expanded ? 'chevron-up' : 'chevron-down'}
                color="gray6"
              />
            </Item>
          </div>

          <Expander expanded={expanded}>
            <div className="content">
              {unselected.map(item => (
                <Item
                  key={item.href}
                  to={item.href}
                  end={item.end ?? true}
                  onClick={() => setExpanded(false)}
                >
                  {item.text}
                </Item>
              ))}
            </div>
          </Expander>
        </Expandable>
      )}
    </Wrapper>
  )
}
